import { ISerializes, IDeserializes } from "./serializable";
import { IEntity } from "./entity";

export interface ILabel extends ISerializes<ILabelExport>, IDeserializes<ILabelImport>, IEntity {
  id: string;
  name: string;
  backgroundColor: string;
  textColor: string;
}

export interface ILabelExport {
  id?: string;
  name: string;
  backgroundColor: string;
}

export interface ILabelImport {
  id: string;
  name: string;
  color?: string;
  backgroundColor: string;
}

export class Label implements ILabel {

  /**
   * The Label ID
   */
  public id: string;

  /**
   * The text (name/title) on the label
   */
  public name: string = null;

  /**
   * The background color for the label
   */
  public get backgroundColor(): string {
    return this._backgroundColor;
  }
  public set backgroundColor(color: string) {
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
      console.warn(`Label color (${color}) is not valid, reverting to default color`);
      color = "#FFFFFF";
    }
    this._backgroundColor = color;

    // Figure out the best text color for the given background
    // This is based on the W3C's algo: ((Red value X 299) + (Green value X 587) + (Blue value X 114)) / 1000
    // https://www.w3.org/TR/AERT/#color-contrast
    const [, rawRed, rawGreen, rawBlue] = color.match(/^#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})$/i);
    const red = parseInt(rawRed, 16);
    const green = parseInt(rawGreen, 16);
    const blue = parseInt(rawBlue, 16);
    this._textColor = (((red * 299) + (green * 587) + (blue * 114)) / 1000) > 125 ? "#000000" : "#FFFFFF";
  }
  protected _backgroundColor = "#FFFFFF";

  /**
   * The appropriate text color
   */
  public get textColor(): string {
    return this._textColor;
  }
  protected _textColor: string;

  constructor(json: Partial<ILabelImport> = {}) {
    this.deserialize(json);
  }

  /**
   * Deserialize JSON into the class
   *
   * @param json Properties to add to the entity
   */
  public deserialize(json: Partial<ILabelImport> = {}): void {
    Object.entries(json).forEach(([key, value]: [string, string]) => {
      this[key] = value;
      if (key === "color") { this.backgroundColor = value; }
    });
    if (!this.name) { this.name = "test label"; } // TEMP FOR TESTING
  }

  /**
   * Serialize for calling API resources
   */
  public toJSON(): ILabelExport {
    return {
      id: this.id,
      name: this.name,
      backgroundColor: this.backgroundColor
    };
  }
}
